<template>
  <Grid gtr="auto 1fr" gap="var(--r-space-md)" class="recommendations-table bg-white-1 shadow3">
    <DepartmentsMenu vuex-module="recommendations" />

    <AgGrid
      v-if="financialPeriodId"
      ref="agGrid"
      :options="options"
      :header-height="48"
      :api-request="infiniteScrollRequest"
      class="q-px-lg q-pb-lg"
      @row-selected="selectionChanged"
      @filter-changed="filterChanged"
      @cell-value-changed="cellValueChanged"
      @new-rows-loaded="newRowsLoaded"
      @ready="onGridReady"
    >
      <template #header>
        <TopRightMenu :ag-grid="agGrid" @open-dialog="openDialog" />
      </template>
    </AgGrid>
  </Grid>
</template>

<script>
import agGridPendingStatusMixin from '@/modules/financial/subpages/predictions-tab/shared/ag-grid-pending-status-mixin'
import * as apiTo from '@/modular-api'
// Mixins
import gridOptions from './grid-options'
// Libraries
import { mapMutations, mapState, mapGetters } from 'vuex'
import { cloneDeep, debounce } from 'lodash'
// Components
import DepartmentsMenu from '@/modules/financial/shared/DepartmentsMenu'
import AgGrid from '@/ag-grid/AgGrid'
import TopRightMenu from './TopRightMenu'
import ConfirmationDialog from '@/modules/financial/shared/ConfirmationDialog'

export default {
  components: {
    DepartmentsMenu,
    AgGrid,
    TopRightMenu
  },

  mixins: [agGridPendingStatusMixin, gridOptions],

  data: () => ({
    agGrid: null,
    justFiltered: false
  }),

  computed: {
    ...mapState('predictions', ['financialPeriodId']),
    ...mapState('recommendations', ['statuses', 'rowsSelected', 'selectedDepartment']),
    ...mapGetters('entity-labels', ['departmentLabel']),

    totalRows: {
      get() {
        return this.$store.state.recommendations.totalRows
      },

      set(newVal) {
        this.setTotalRows(newVal)
      }
    },

    rowsSelected: {
      get() {
        return this.$store.state['recommendations'].rowsSelected
      },

      set(newVal) {
        this.setRowsSelected(newVal)
      }
    },

    selectAll: {
      get() {
        return this.$store.state.recommendations.selectAll
      },
      set(newVal) {
        this.setSelectAll(newVal)
      }
    },

    filterInProgress: {
      get() {
        return this.$store.state['recommendations'].filterInProgress
      },
      set(newVal) {
        this.setFilterInProgress(newVal)
      }
    }
  },

  watch: {
    financialPeriodId() {
      this.setRowsSelected([])
      this.setSelectAll(false)
      this.$emit('rerender-table')
    },

    selectAll(newVal) {
      if (this.justFiltered) return
      this.rowsSelected = []
      this.$refs.agGrid.selectOrDeselectAll(newVal)
    },

    rowsSelected(newVal) {
      if (!this.totalRows) return

      if (newVal.length === this.totalRows && this.selectAll) {
        // if selectAll checkbox is checked and all rows are unchecked
        // clear selectAll and selected rows
        this.rowsSelected = []
        this.selectAll = false
      } else if (newVal.length === this.totalRows && !this.selectAll) {
        // if all rows are selected check the selectAll checkbox
        this.selectAll = true
      }
    },

    selectedDepartment() {
      if (this.agGrid) this.purgeServerSideCache()
    }
  },

  methods: {
    ...mapMutations('recommendations', [
      'setTotalRows',
      'setRowsSelected',
      'setSelectAll',
      'refreshCards',
      'setFilterInProgress'
    ]),

    infiniteScrollRequest(params) {
      return apiTo.GET_PAGINATED_SUBSCRIPTIONS_PERIODS_FOR_FINANCIAL_PERIOD(
        this.financialPeriodId,
        params,
        'recommendationStatus,stakeholdersCount,positiveStakeholdersCount',
        { 'filter[departments.id]': this.selectedDepartment?.id }
      )
    },

    selectionChanged: debounce(function({ row, isSelected }) {
      const clonedRow = cloneDeep(row)

      if (this.selectAll) {
        if (!isSelected) this.rowsSelected = [...this.rowsSelected, clonedRow]
        else this.rowsSelected = this.rowsSelected.filter(selectedRow => selectedRow.id !== row.id)
      } else {
        if (!isSelected) this.rowsSelected = this.rowsSelected.filter(selectedRow => selectedRow.id !== row.id)
        else this.rowsSelected = [...this.rowsSelected, clonedRow]
      }
    }, 0),

    filterChanged() {
      this.filterInProgress = true
      this.justFiltered = true
      this.selectAll = false
      this.rowsSelected = []
    },

    newRowsLoaded({ totalRows, newRows }) {
      this.filterInProgress = false
      const newRowsIds = newRows.map(row => row.id)

      this.totalRows = totalRows
      if (this.justFiltered) {
        this.agGrid.api.forEachNode(node => {
          if (newRowsIds.includes(node.id)) node.setSelected(false)
        })
        this.justFiltered = false
      } else if (this.selectAll) {
        this.agGrid.api.forEachNode(node => {
          if (newRowsIds.includes(node.id)) node.setSelected(true)
        })
      }
    },

    cellValueChanged(event) {
      if (event.oldValue === event.newValue) return

      const newStatus = this.statuses.find(s => s.name === event.newValue)
      const payload = { recommendation_status_id: newStatus.id }
      const oldData = {
        ...event.data,
        recommendation_status: { id: event.data.recommendation_status.id, name: event.oldValue }
      }
      this.updateStatus(event.data.id, payload, oldData)
    },

    updateStatus(id, payload, oldData) {
      apiTo
        .MODIFY_SUBSCRIPTION_PERIOD_FOR_FINANCIAL_PERIOD(
          id,
          payload,
          'recommendationStatus,stakeholdersCount,positiveStakeholdersCount'
        )
        .then(({ data }) => {
          this.$refs.agGrid.updateRow(id, data.result)
          this.refreshCards()
        })
        .catch(() => {
          this.$refs.agGrid.updateRow(id, oldData)
        })
    },

    openDialog({ status, numberOfSelectedRows }) {
      this.$q
        .dialog({
          component: ConfirmationDialog,
          parent: this,
          subsCount: numberOfSelectedRows,
          status,
          filterModel: this.agGrid.api.getFilterModel(),
          vuexModule: 'recommendations'
        })
        .onOk(() => {
          this.refreshCards()
          const idsToUpdate = this.agGrid.api.getSelectedNodes().map(node => node.data.id)

          this.agGrid.api.forEachNode(rowNode => {
            if (idsToUpdate.indexOf(rowNode.data.id) >= 0 && rowNode.data.status.name === 'pending') {
              rowNode.setData({ ...rowNode.data, recommendation_status: { ...status } })
            }
          })

          this.selectAll = false
          this.rowsSelected = []
          this.$refs.agGrid.selectOrDeselectAll(false)
        })
    },

    purgeServerSideCache() {
      this.filterChanged()
      this.$refs.agGrid.purgeServerSideCache()
    }
  }
}
</script>

<style lang="scss" scoped>
.recommendations-table {
  grid-area: table;
  min-height: 630px;
}
</style>
